import {
  EOrigin,
  ICollectionFlow,
  ICollectionFlowDTO,

  IFacility, IFacilityAdvancedSettings,
  IFacilityAutoApplyDTO,
  IFacilityAutoApplySettings,
  IFacilityCollectionFlowsFilters,
  IFacilityCollectionFlowsResult,
  IFacilityCollectionSettings,
  IFacilityFiltersDTO,
  IFacilityPayerTypePaymentSetting,
  IFacilityPaymentAccount,
  IFacilityStatisticsDTO,
  IPayerType,
  IPointClickCareCredentials,
  ISetupWizardFacilityStatementSettings,
  ITemplate,
  IUpdateFacilityFromPccDTO,
  IClaimFlowDTO,
  IFacilityClaimFlowsResult,
  IFacilityClaimFlowsFilters,
  IClaim,
  IClaimTask,
  IFacilityBillingSettings,
  ICommissionsSettings,
  IFacilityPayerGroup,
  IGetUploadFacilityDataTaskFilters,
  IManualPCCToggles,
  IPccUploadTask
} from '@zunta/configurations'
import { IBaseStore } from '../types'
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import Api from '../../api'
import { FacilityCompletedLevels } from '../types/facility'

export interface IFacilityState {
  facilitiesForImport: { name: string, originId: string }[];
  isImported: boolean;
  facilities: IFacility[],
  total: number;
  facilities2: IFacility[],
  total2: number;
  selectedFacility: IFacility | null;
  statistics: IFacilityStatisticsDTO | null;
  payerTypes: IPayerType[];
  selectedFacilitiesOriginIdsForImport: string[];
  facilitiesActivePayerTypesMap: {[k: string]: IPayerType[]} | null;
  templates: ITemplate[];
  selectedTemplate: ITemplate | null;
  templatesTotal: number;
  selectedCollectionFlowData: ICollectionFlowDTO | null;
  collectionFlowTableItems: IFacilityCollectionFlowsResult[];
  collectionFlowLastQuery: IFacilityCollectionFlowsFilters & { facility?: number | string };
  collectionFlowTableTotal: number;
  collectionFlows: ICollectionFlow[];
  facilityCollectionSettings: IFacilityCollectionSettings | null;
  facilityPaymentAccount: IFacilityPaymentAccount | null;
  facilitiesPaymentAccounts: IFacilityPaymentAccount[] | null;
  autoApplySettings: Partial<IFacilityAutoApplySettings> | null;
  payerTypePaymentSettings: Partial<IFacilityPayerTypePaymentSetting>[];
  facilitiesAllPayerTypesMap: { [k: string]: IPayerType[] } | null;
  facilityAdvancedSettings: IFacilityAdvancedSettings[];

  selectedClaimFlowData: IClaimFlowDTO | null;
  claimFlowTableItems: IFacilityClaimFlowsResult[];
  claimFlowLastQuery: IFacilityClaimFlowsFilters & { facility?: number | string };

  claims: IClaim[];
  claimsTotal: number;

  selectedClaimTask: IClaimTask | null;
  claimTasks: IClaim[];
  claimTasksTotal: number;

  willowPayers: any[];
  billingSettings: IFacilityBillingSettings | null;

  commissionsSettings: ICommissionsSettings[];

  facilityPayerGroups: IFacilityPayerGroup[];
}

export interface IFacilityStore extends IBaseStore {
  state: IFacilityState
}

@Module({
  name: 'facility',
  namespaced: true
})
export class FacilityModule extends VuexModule {
  facilitiesForImport: { name: string, originId: string }[] = [];
  facilityCompleteLevels: FacilityCompletedLevels = {}
  selectedFacilitiesOriginIdsForImport: string[] = [];
  isImported: boolean = false;
  facilities: IFacility[] = [];
  total: number = 0;
  facilities2: IFacility[] = [];
  total2: number = 0;
  selectedFacility: IFacility | null = null;
  pccPassword: string = '';
  pccUsername: string = '';
  useNewPCCLogin: boolean = false;
  useManualPCCImport: boolean = false;
  useManualPCCPush: boolean = false;
  statistics: IFacilityStatisticsDTO | null = null;
  payerTypes: IPayerType[] = [];
  facilitiesActivePayerTypesMap: { [k: string]: IPayerType[] } | null = null;
  facilitiesAllPayerTypesMap: { [k: string]: IPayerType[] } | null = null;
  facilityPaymentAccount: IFacilityPaymentAccount | null = null;
  facilitiesPaymentAccounts: IFacilityPaymentAccount[] | null = null
  selectedCollectionFlowData: ICollectionFlowDTO | null = null;
  collectionFlowTableItems: IFacilityCollectionFlowsResult[] = []
  collectionFlowLastQuery: IFacilityCollectionFlowsFilters & { facility?: number | string } = {};
  collectionFlowTableTotal: number = 0;
  collectionFlows: ICollectionFlow[] = [];
  facilityCollectionSettings: IFacilityCollectionSettings | null = null;
  autoApplySettings: Partial<IFacilityAutoApplySettings> | null = null;
  payerTypePaymentSettings: Partial<IFacilityPayerTypePaymentSetting>[] = [];
  facilityAdvancedSettings: IFacilityAdvancedSettings[] = [];

  selectedClaimFlowData: IClaimFlowDTO | null = null;
  claimFlowTableItems: IFacilityClaimFlowsResult[] = [];
  claimFlowTableTotal: number = 0;
  claimFlowLastQuery: IFacilityClaimFlowsFilters = {};

  claims: IClaim[] = [];
  claimsTotal: number = 0;

  selectedClaimTask: IClaimTask | null = null;
  claimTasks: IClaimTask[] = [];
  claimTasksTotal: number = 0;

  willowPayers: any[] = [];
  billingSettings: IFacilityBillingSettings | null = null;
  commissionsSettings: ICommissionsSettings[] = [];

  facilityPayerGroups: IFacilityPayerGroup[] = []

  uploadFacilityTasks: any[] = []
  totalUploadFacilityTasks: number = 0

  @Mutation
  SET_COMMISSIONS_SETTINGS (settings: ICommissionsSettings[]) {
    this.commissionsSettings = settings
  }

  @Mutation
  SET_FACILITY_COMPLETE_LEVEL (levels: { type: keyof FacilityCompletedLevels, value: number }[]) {
    const updatedFacilityCompleteLevel: FacilityCompletedLevels = { ...this.facilityCompleteLevels }
    levels.forEach((level) => {
      const { type, value } = level
      updatedFacilityCompleteLevel[type] = value
    })
    this.facilityCompleteLevels = updatedFacilityCompleteLevel
  }

  @Mutation
  SET_BILLING_SETTINGS (settings: IFacilityBillingSettings | null) {
    this.billingSettings = settings
  }

  @Mutation
  SET_SELECTED_CLAIM_TASK (claimTask: IClaimTask | null) {
    this.selectedClaimTask = claimTask
  }

  @Mutation
  SET_FACILITIES_PAYMENT_ACCOUNTS (data: IFacilityPaymentAccount[] | null) {
    this.facilitiesPaymentAccounts = data
  }

  @Mutation
  UPDATE_SELECTED_CLAIM_TASK (data: Partial<IClaimTask>) {
    if (this.selectedClaimTask) {
      Object.assign(this.selectedClaimTask, data)
    }
  }

  @Mutation
  UPDATE_CLAIM_TASK_IN_LIST (input: { id: number | string, data: Partial<IClaimTask>, replacements?: Partial<IClaimTask> }) {
    const claimTask = this.claimTasks.find((claimTask) => claimTask.id === parseInt(input.id.toString()))
    const dataToAssign = input.replacements || input.data
    if (claimTask) {
      Object.assign(claimTask, dataToAssign)
    }
  }

  @Mutation
  SET_CLAIM_TASKS (claimTasks: IClaimTask[]) {
    this.claimTasks = claimTasks
  }

  @Mutation
  SET_CLAIM_TASKS_TOTAL (total: number) {
    this.claimTasksTotal = total
  }

  @Mutation
  SET_CLAIMS (claims: IClaim[]) {
    this.claims = claims
  }

  @Mutation
  SET_CLAIMS_TOTAL (total: number) {
    this.claimsTotal = total
  }

  @Mutation
  SET_FACILITIES_ALL_PAYER_TYPE_MAP (map: { [k: string]: IPayerType[] }) {
    this.facilitiesAllPayerTypesMap = map
  }

  @Mutation
  SET_AUTO_APPLY_SETTINGS (settings: Partial<IFacilityAutoApplySettings> | null) {
    this.autoApplySettings = settings
  }

  @Mutation
  SET_PAYER_TYPE_PAYMENT_SETTINGS (settings: Partial<IFacilityPayerTypePaymentSetting>[]) {
    this.payerTypePaymentSettings = settings
  }

  @Mutation
  SET_FACILITY_PAYMENT_ACCOUNT (data: IFacilityPaymentAccount | null) {
    this.facilityPaymentAccount = data
  }

  @Mutation
  SET_FACILITY_COLLECTION_SETTINGS (settings: IFacilityCollectionSettings | null) {
    this.facilityCollectionSettings = settings
  }

  @Mutation
  SET_COLLECTION_FLOWS (collectionFlows: ICollectionFlow[]) {
    this.collectionFlows = collectionFlows
  }

  @Mutation
  SET_COLLECTION_FLOW_TABLE_ITEMS (items: IFacilityCollectionFlowsResult[]) {
    this.collectionFlowTableItems = items
  }

  @Mutation
  SET_CLAIM_FLOW_TABLE_ITEMS (items: IFacilityClaimFlowsResult[]) {
    this.claimFlowTableItems = items
  }

  @Mutation
  SET_CLAIM_FLOW_TABLE_TOTAL (total: number) {
    this.claimFlowTableTotal = total
  }

  @Mutation
  SET_CLAIM_FLOW_LAST_QUERY (query: IFacilityClaimFlowsFilters & { facility?: number | string }) {
    this.claimFlowLastQuery = query
  }

  @Mutation
  SET_COLLECTION_FLOW_LAST_QUERY (query: IFacilityCollectionFlowsFilters & { facility?: number | string }) {
    this.collectionFlowLastQuery = query
  }

  @Mutation
  SET_COLLECTION_FLOW_TABLE_TOTAL (total: number) {
    this.collectionFlowTableTotal = total
  }

  @Mutation
  SET_SELECTED_COLLECTION_FLOW (data: ICollectionFlowDTO | null) {
    this.selectedCollectionFlowData = data
  }

  @Mutation
  SET_SELECTED_CLAIM_FLOW (data: IClaimFlowDTO | null) {
    this.selectedClaimFlowData = data
  }

  @Mutation
  SET_WILLOW_PAYERS (data: any) {
    this.willowPayers = data
  }

  @Mutation
  SET_SELECTED_FACILITIES_FOR_IMPORT (selectedFacilitiesOriginIdsForImport: string[]) {
    this.selectedFacilitiesOriginIdsForImport = selectedFacilitiesOriginIdsForImport
  }

  @Mutation
  SET_FACILITIES_ACTIVE_PAYER_TYPE_MAP (map: {[k: string]: IPayerType[]} | null) {
    this.facilitiesActivePayerTypesMap = map
  }

  @Mutation
  SET_FACILITIES_FOR_IMPORT (facilities: { name: string, originId: string }[]) {
    this.isImported = true
    this.facilitiesForImport = facilities
  }

  @Mutation
  SET_PCC_CREDENTIALS (c: { username: string, password: string, useNewPCCLogin: boolean}) {
    this.pccUsername = c.username
    this.pccPassword = c.password
    this.useNewPCCLogin = c.useNewPCCLogin
  }

  @Mutation
  UPDATE_FACILITY_LIST (facilities: IFacility[]) {
    this.facilities = facilities
  }

  @Mutation
  UPDATE_FACILITY_LIST_2 (facilities: IFacility[]) {
    this.facilities2 = facilities
  }

  @Mutation
  ADD_FACILITY (facility: IFacility) {
    this.facilities.push(facility)
  }

  @Mutation
  UPDATE_FACILITIES_TOTAL (total: number) {
    this.total = total
  }

  @Mutation
  UPDATE_FACILITIES_TOTAL_2 (total: number) {
    this.total2 = total
  }

  @Mutation
  UPDATE_SELECTED_FACILITY (facility: IFacility | null) {
    this.selectedFacility = facility
  }

  @Mutation
  UPDATE_SELECT_FACILITY_PROP (input: { propName: string, value: any }) {
    (this.selectedFacility as any)[input.propName] = input.value
  }

  @Mutation
  RESET_SELECTED_FACILITY () {
    this.selectedFacility = null
  }

  @Mutation
  UPDATE_SELECTED_FACILITY_INFO (data: Partial<IFacility>) {
    if (this.selectedFacility) {
      Object.keys(data).forEach((key: string) => {
        (this.selectedFacility as any)[key] = (data as any)[key]
      })
      this.selectedFacility.password = ''
    }
  }

  @Mutation
  UPDATE_FACILITIES_STATISTICS (statistics: IFacilityStatisticsDTO) {
    this.statistics = statistics
  }

  @Mutation
  UPDATE_PAYER_TYPES (payerTypes: IPayerType[]) {
    this.payerTypes = payerTypes
  }

  @Mutation
  UPDATE_PAYER_TYPE_STATUS (input: { payerTypeId: number, status: boolean }) {
    this.payerTypes = [...this.payerTypes.map((payerType: IPayerType) => {
      if (payerType.id === input.payerTypeId) {
        payerType.active = input.status
      }
      return payerType
    })]
  }

  @Mutation
  SET_FACILITY_ADVANCED_SETTINGS (settings: IFacilityAdvancedSettings[]) {
    this.facilityAdvancedSettings = settings
  }

  @Mutation
  SET_FACILITY_PAYER_GROUPS (groups: any[]) {
    this.facilityPayerGroups = groups
  }

  @Mutation
  SET_UPLOAD_FACILITY_TASKS (tasks: IPccUploadTask[]) {
    this.uploadFacilityTasks = tasks
  }

  @Mutation
  SET_TOTAL_UPLOAD_FACILITY_TASKS (total: number) {
    this.totalUploadFacilityTasks = total
  }

  @Action
  async getUploadFacilityTasks (filters: IGetUploadFacilityDataTaskFilters) {
    const { taskList, total } = await Api.facility.getPccUpdateRequestsList(filters)
    this.SET_UPLOAD_FACILITY_TASKS(taskList)
    this.SET_TOTAL_UPLOAD_FACILITY_TASKS(total)
  }

  @Action
  async saveCommissionsSettings (input: { settings: Partial<ICommissionsSettings>[], facilityId: number | string }) {
    return Api.billing.saveCommissionsSettings(input.facilityId, input.settings)
  }

  @Action
  async fetchCommissionsSettings (facilityId: number) {
    const settings = await Api.billing.getCommissionsSettings(facilityId)
    this.SET_COMMISSIONS_SETTINGS(settings)
  }

  @Action
  async fetchFacilityBillingSettings (facilityId: number) {
    const settings = await Api.billing.getFacilityBiilingSettings(facilityId)
    this.SET_BILLING_SETTINGS(settings)
  }

  @Action
  async saveFacilityBillingSettings (data: Partial<IFacilityBillingSettings>) {
    const settings = await Api.billing.saveSettings(data)
    await this.fetchFacilityBillingSettings(settings.facility as number)
  }

  @Action
  async fetchClaimsByFilters (filters: any) {
    const { claims, total } = await Api.facility.getClaimsByFilters(filters)
    this.SET_CLAIMS(claims)
    this.SET_CLAIMS_TOTAL(total)
  }

  @Action
  async setSelectedFacilitiesForImport (selectedFacilitiesOriginIdsForImport: string[]) {
    this.SET_SELECTED_FACILITIES_FOR_IMPORT(selectedFacilitiesOriginIdsForImport)
  }

  @Action
  async fetchClaimTasks (filters: any) {
    const { total, claimTasks } = await Api.facility.getClaimTasksByFIlter(filters)
    this.SET_CLAIM_TASKS(claimTasks)
    this.SET_CLAIM_TASKS_TOTAL(total)
  }

  @Action
  resetSelectedClaimTask () {
    this.SET_SELECTED_CLAIM_TASK(null)
  }

  @Action
  async fetchClaimTaskById (id: string | number) {
    const task = await Api.facility.getClaimTaskById(id)
    this.SET_SELECTED_CLAIM_TASK(task)
  }

  @Action
  async getAllFacilitiesByPointClickCareCredentials (c: IPointClickCareCredentials)
  : Promise<boolean> {
    const { password, username, useNewPCCLogin } = c
    this.SET_PCC_CREDENTIALS({ username, password, useNewPCCLogin: !!useNewPCCLogin })
    const facilities: { name: string, originId: string }[] = await Api.facility.getFacilitiesByPointClickCareCredentials(c)
    const isResponseOk = !!facilities && Array.isArray(facilities)
    if (isResponseOk) {
      this.SET_FACILITIES_FOR_IMPORT(facilities)
    }
    return isResponseOk
  }

  @Action
  async fetchFacilityUpdates (input: { facilityId: number | string, options: IUpdateFacilityFromPccDTO }): Promise<'OK'> {
    const response = await Api.facility.fetchFacilityUpdates(input.facilityId, input.options)
    return response
  }

  @Action
  async importSelectedFacilities () :Promise<'OK'> {
    if (this.selectedFacilitiesOriginIdsForImport.length > 0) {
      console.log('this use useNewPCCLogin', this.useNewPCCLogin)
      const password = this.pccPassword
      const username = this.pccUsername
      const cred: IPointClickCareCredentials = {
        username,
        password,
        useNewPCCLogin: this.useNewPCCLogin,
        facilities: this.selectedFacilitiesOriginIdsForImport
      }
      const res = await Api.facility.fetchFacilitiesWithDetails(cred)
      await Api.refreshToken()
      return res
    }
    return 'OK'
  }

  @Action
  async updateFacilityInfoFromPcc (input: {
    username: string,
    password: string,
    facilityId: string
  }): Promise<'OK'> {
    const cred: IPointClickCareCredentials = {
      username: input.username,
      password: input.password,
      facilities: [input.facilityId]
    }
    const res = await Api.facility.fetchFacilitiesWithDetails(cred)
    return res
  }

  @Action
  async deleteFacility (facilityId: number | string): Promise<'OK'> {
    const res = await Api.facility.deleteFacility(facilityId)
    return res
  }

  @Action
  async getFacilityList (query: IFacilityFiltersDTO): Promise<void> {
    const response = await Api.facility.getFacilityList(query)
    const { facilities, total } = response
    this.UPDATE_FACILITIES_TOTAL(total)
    this.UPDATE_FACILITY_LIST(facilities || [])
  }

  @Action
  async getFacilityList2 (query: IFacilityFiltersDTO): Promise<void> {
    const response = await Api.facility.getFacilityList(query)
    const { facilities, total } = response
    this.UPDATE_FACILITIES_TOTAL_2(total)
    this.UPDATE_FACILITY_LIST_2(facilities || [])
  }

  @Action
  async getFacilityListAndReturn (query: IFacilityFiltersDTO): Promise<IFacility[]> {
    const response = await Api.facility.getFacilityList(query)
    const { facilities } = response
    return facilities
  }

  @Action
  async getFacility (facilityId: number | string | undefined): Promise<void> {
    if (facilityId) {
      const { facility } = await Api.facility.getFacility(facilityId)
      this.UPDATE_SELECTED_FACILITY(facility)
    }
  }

  @Action
  async fetchStatistics () {
    const statistics = await Api.statistics.getFacilityStatistics()
    if (!statistics.statusCode) {
      this.UPDATE_FACILITIES_STATISTICS(statistics)
    }
  }

  @Action
  resetSelectedFacility () {
    this.RESET_SELECTED_FACILITY()
  }

  @Action
  async updateFacility (input: { id: number, data: Partial<IFacility> }) {
    const response = await Api.facility.updateFacility(input.id, input.data)
    if (typeof response === 'boolean') {
      this.UPDATE_SELECTED_FACILITY_INFO(input.data)
    }
    return response
  }

  @Action
  async fetchPayerTypes (facilityId: number | string) {
    const payerTypes = await Api.facility.getFacilityPayers(facilityId)
    this.UPDATE_PAYER_TYPES(payerTypes)
  }

  // @Action
  // async changePayerTypeStatus (input: { payerTypeId: number, status: boolean }) {
  //   const response = await Api.facility.changePayerTypeStatus(input.payerTypeId, input.status)
  //   if (response === 'OK') {
  //     this.UPDATE_PAYER_TYPE_STATUS(input)
  //   }
  // }

  @Action
  resetPayerTypes () {
    this.UPDATE_PAYER_TYPES([])
  }

  @Action
  async uploadLogo (input: { facilityId: number, logo: any }) {
    const newLogo = await Api.facility.uploadLogo(input.facilityId, input.logo)
    this.UPDATE_SELECT_FACILITY_PROP({ propName: 'logo', value: newLogo })
  }

  @Action
  async updateClaimTask (input: { id: number | string, data: Partial<IClaimTask>, replacements?: Partial<IClaimTask> }) {
    await Api.facility.updateClaimTask(input.id, input.data)
    this.UPDATE_CLAIM_TASK_IN_LIST(input)
    this.UPDATE_SELECTED_CLAIM_TASK(input.replacements || input.data)
  }

  @Action
  async createFacility (facilityData: IFacility) {
    const facility = await Api.facility.createFacility(facilityData)
    this.ADD_FACILITY(facility)
    await Api.refreshToken()
    return facility
  }

  @Action
  async toggleManualPCCForChain (facilityData: IManualPCCToggles) {
    await Api.facility.toggleManualPCCForChain(facilityData)
  }

  @Action
  async fetchFacilitiesActivePayerTypesMap (facilities: number[]) {
    const map = await Api.facility.getFacilitiesActivePayerTypesMap(facilities)
    this.SET_FACILITIES_ACTIVE_PAYER_TYPE_MAP(map)
  }

  @Action
  async fetchAllPayerTypesMap () {
    const map = await Api.facility.getAllPayerTypesMap()
    this.SET_FACILITIES_ALL_PAYER_TYPE_MAP(map)
  }

  @Action
  resetFacilitiesActivePayerTypesMap () {
    this.SET_FACILITIES_ACTIVE_PAYER_TYPE_MAP(null)
  }

  @Action
  async setupPayerTypes (payerTypes: IPayerType[]) {
    const response = await Api.facility.setupPayerTypes(payerTypes)
    return response
  }

  @Action
  async fetchFacilityResidents (facilityId: number | string) {
    const response = await Api.resident.fetchFacilityResidents(facilityId)
    return response
  }

  @Action
  async updatePayerTypes (input: { facilityId: number | string, payerTypes: IPayerType[] }) {
    const response = await Api.facility.updatePayerTypes(input.facilityId, input.payerTypes)
    this.UPDATE_PAYER_TYPES(response)
  }

  @Action
  async createCollectionFlow (input: { facilityId: number | string, data: ICollectionFlowDTO }) {
    const response = await Api.collectionFlow.createCollectionFlow(input.facilityId, input.data)
    return response
  }

  @Action
  async createCollectionFlows (input: { facilityIds: number[], data: ICollectionFlowDTO }) {
    const response = await Api.collectionFlow.createCollectionFlows(input.facilityIds, input.data)
    return response
  }

  @Action
  async updateCollectionFlow (input: { collectionFlowId: number | string, data: ICollectionFlowDTO }) {
    const response = await Api.collectionFlow.updateCollectionFlow(input.collectionFlowId, input.data)
    return response
  }
  @Action
  async getCollectionFlowById (collectionFlowId: number | string) {
    const response = await Api.collectionFlow.getCollectionFlowById(collectionFlowId)
    this.SET_SELECTED_COLLECTION_FLOW(response)
  }

  @Action
  async resetSelectedCollectionFlowData () {
    this.SET_SELECTED_COLLECTION_FLOW(null)
  }

  @Action
  async resetSelectedClaimFlowData () {
    this.SET_SELECTED_CLAIM_FLOW(null)
  }

  @Action
  async fetchCollectionFlowTableItems (input: { facilityId: string | number, filters: IFacilityCollectionFlowsFilters }) {
    const { total, items } = await Api.collectionFlow.getFacilityCollectionFlows(input.facilityId, input.filters)
    this.SET_COLLECTION_FLOW_LAST_QUERY({
      ...input.filters,
      facility: input.facilityId
    })
    this.SET_COLLECTION_FLOW_TABLE_ITEMS(items)
    this.SET_COLLECTION_FLOW_TABLE_TOTAL(total)
  }

  @Action
  async fetchAllCollectionFlows () {
    const collectionFlows = await Api.collectionFlow.getAllCollectionFlows()
    this.SET_COLLECTION_FLOWS(collectionFlows)
  }

  @Action
  async deleteCollectionFlow (collectionFlowId: number | string) {
    const response = await Api.collectionFlow.deleteCollectionFlow(collectionFlowId)
    if (response === 'OK' && this.collectionFlowLastQuery.facility) {
      this.fetchCollectionFlowTableItems({
        facilityId: this.collectionFlowLastQuery.facility,
        filters: this.collectionFlowLastQuery
      })
    }
    return response
  }

  @Action
  async updateFacilityCollectionSettings (input: { facilityId: number | string, data: Partial<IFacilityCollectionSettings> }) {
    await Api.facility.updateFacilityCollectionSettings(input.facilityId, input.data)
  }

  @Action
  async getFacilityCollectionSettings (facilityId: number | string) {
    const settings = await Api.facility.getFacilityCollectionSettings(facilityId)
    this.SET_FACILITY_COLLECTION_SETTINGS(settings)
  }

  @Action
  async saveFacilityPaymentAccount (input: { data: Partial<IFacilityPaymentAccount>, voidedCheckFile: File | null, withdrawnVoidedCheckFile: File | null }) {
    const response: any = await Api.facility.saveFacilityPaymentAccount('review', input.data, input.voidedCheckFile, input.withdrawnVoidedCheckFile)
    if (response.statusCode) {
      return { status: 'Failed', message: response.message || ['Something Went Wrong'] }
    }
    return { status: 'OK', popupToken: response }
  }

  @Action
  async saveFacilityPaymentAccountDraft (input: { data: Partial<IFacilityPaymentAccount>, voidedCheckFile: File | null, withdrawnVoidedCheckFile: File | null }) {
    const response: any = await Api.facility.saveFacilityPaymentAccount('save', input.data, input.voidedCheckFile, input.withdrawnVoidedCheckFile)
    if (response.statusCode) {
      return { status: 'Failed', message: response.message || ['Something Went Wrong'] }
    }
    return { status: 'OK', popupToken: null }
  }

  @Action
  async fetchFacilityPaymentAccount (facilityId: number) {
    const paymentAccount = await Api.facility.getFacilityPaymentAccount(facilityId)
    this.SET_FACILITY_PAYMENT_ACCOUNT(paymentAccount)
  }

  @Action
  async fetchFacilitiesPaymentAccounts (facilityIds: number[]) {
    const paymentAccounts = await Api.facility.getFacilityPaymentAccounts(facilityIds)
    this.SET_FACILITIES_PAYMENT_ACCOUNTS(paymentAccounts)
  }

  @Action
  async fetchFacilityAutoApplySettings (facilityId: string | number) {
    const response: IFacilityAutoApplyDTO | null = await Api.facility.getFacilityAutoApplySettings(facilityId)
    if (response && !(response as any).statusCode) {
      const { settings, payerTypeSettings } = response
      this.SET_AUTO_APPLY_SETTINGS(settings)
      this.SET_PAYER_TYPE_PAYMENT_SETTINGS(payerTypeSettings)
      return 'OK'
    }
    return 'Failed'
  }

  @Action
  async saveFacilityAutoApplySettings (data: IFacilityAutoApplyDTO) {
    const response: IFacilityAutoApplyDTO = await Api.facility.saveFacilityAutoApplySettings(data)
    if (response && !(response as any).statusCode) {
      const { settings, payerTypeSettings } = response
      this.SET_AUTO_APPLY_SETTINGS(settings)
      this.SET_PAYER_TYPE_PAYMENT_SETTINGS(payerTypeSettings)
      return 'OK'
    }
    return 'Failed'
  }

  @Action
  async uploadSelectedFacilitiesData (facilitiesData: any[]) {
    await Api.facility.uploadSelectedFacilitiesData(this.pccUsername, this.pccPassword, facilitiesData)
  }

  @Action
  resetAutoApplySettings () {
    this.SET_AUTO_APPLY_SETTINGS(null)
    this.SET_PAYER_TYPE_PAYMENT_SETTINGS([])
  }

  @Action
  async setupFacilityStatementSettings (statementSettings: ISetupWizardFacilityStatementSettings) {
    const response = await Api.facility.setupFacilityStatementSettings(statementSettings)
    return response
  }
  @Action
  async setPaymentAccountEnableEditing (data: any) {
    const account = await Api.facility.setPaymentAccountEnableEditing(data.facilityId, data.enableEditing)
    if (account) {
      this.SET_FACILITY_PAYMENT_ACCOUNT(account)
    }
  }

  @Action
  async fetchFacilityAdvancedSettings (facilityId: number) {
    const settings = await Api.facility.fetchFacilityAdvancedSettings(facilityId)
    this.SET_FACILITY_ADVANCED_SETTINGS(settings)
    return settings
  }

  @Action
  async saveFacilityAdvancedSettings (data: any) {
    const settings = await Api.facility.saveFacilityAdvancedSettings(data.facilityId, data.settings)
    this.SET_FACILITY_ADVANCED_SETTINGS(settings)
    return settings
  }

  @Action
  async deleteFacilityAdvancedSetting (data: any) {
    await Api.facility.deleteFacilityAdvancedSetting(data.facilityId, data.settingId)
  }

  @Action
  async getAllFacilitiesPayerTypes (filters: any) {
    return Api.facility.getAllFacilitiesPayerTypes(filters)
  }

  @Action
  async getWillowPayers (filters: any) {
    const res = await Api.facility.getWillowPayers(filters)
    this.SET_WILLOW_PAYERS(res)
  }

  @Action
  async getClaimFlow (flowId: number) {
    const res = await Api.facility.getClaimFlow(flowId)
    this.SET_SELECTED_CLAIM_FLOW(res)
  }

  @Action
  async getFacilityClaimFlows (data: any) {
    const { total, items } = await Api.facility.getFacilityClaimFlows(data.facilityId, data.filters)
    this.SET_CLAIM_FLOW_TABLE_ITEMS(items)
    this.SET_CLAIM_FLOW_TABLE_TOTAL(total)
  }

  @Action
  async createClaimFlow (data: any) {
    return Api.facility.createClaimFlow(data)
  }

  @Action
  async updateClaimFlow (data: any) {
    return Api.facility.updateClaimFlow(data)
  }

  @Action
  async deleteClaimFlow (flowId: number) {
    return Api.facility.deleteClaimFlow(flowId)
  }

  @Action
  async getFacilityPayerGroups (facilityId: number) {
    const res = await Api.facility.getFacilityPayerGroups(facilityId)
    this.SET_FACILITY_PAYER_GROUPS(res)
  }

  @Action
  async createFacilityPayerGroup (group: Partial<IFacilityPayerGroup>) {
    return Api.facility.createFacilityPayerGroup(group)
  }

  @Action
  async updateFacilityPayerGroup (group: {facilityPayerGroupId: number, group: Partial<IFacilityPayerGroup>}) {
    return Api.facility.updateFacilityPayerGroup(group.facilityPayerGroupId, group.group)
  }

  @Action
  async deleteFacilityPayerGroup (facilityPayerGroupId: number) {
    return Api.facility.deleteFacilityPayerGroup(facilityPayerGroupId)
  }

  @Action
  async patchFacilityAdvancedSettings (data: any) {
    return Api.facility.patchFacilityAdvancedSettings(data.id, data.setting)
  }

  get facilitiesForImportList (): { name: string, originId: string }[] {
    return this.facilitiesForImport
  }

  get isSelectedPCC () {
    return this.selectedFacility && this.selectedFacility.origin === EOrigin.PCC
  }

  get activePayerTypes () {
    if (!this.payerTypes) {
      return []
    }
    return this.payerTypes.filter((pt) => pt.active).map((pt) => ({
      payerType: pt.id,
      payerName: pt.payer_name
    }))
  }
}
